/**
 * Arranque de la PWA de planta. La estación la define el dispositivo enrolado;
 * el flujo de verde abre directo, cero toques, con la estación siempre visible
 * en el encabezado (análisis §5.2). Config del encargado: mantener apretado el
 * encabezado 3 segundos — nunca a un toque del camino feliz.
 * v3: verde tiene dos registros (viaje y descarte de recepción); sin fotos.
 */

import { abrirBd, actualizarPreferencias, leerConfig, type CatalogosLocales, type Preferencias, type Sesion } from './datos/bd';
import { bandeja, contarPendientes, historiaDeProduccion, mutacionesDeAvisos, mutacionesDeCalidad, mutacionesDeVerde, mutacionesDeEmbolsado, mutacionesDeProduccion, purgarRecibosViejos, recibos } from './datos/cola';
import { contarFotos } from './datos/fotos';
import { reintentarEnrolamientoPendiente } from './datos/enrolar';
import { crearPlanificador, leerCatalogos, sincronizar } from './datos/sync';
import type { BorradorEmbolsado } from './nucleo/embolsado';
import type { BorradorProduccion } from './nucleo/produccion';
import { horaCorta, MINUTO_MS } from './nucleo/tiempo';
import { conAvisoDeReloj } from './ui/aviso-reloj';
import { el, icono } from './ui/componentes';
import {
	acciones,
	ahoraMs,
	app,
	descartarBorrador,
	estacionDeBorrador,
	guardarBorrador,
	iniciarApp,
	irA,
	listarBorradores,
	nuevoBorrador,
	nuevoBorradorAbrir,
	nuevoBorradorAviso,
	nuevoBorradorCambiarLetra,
	nuevoBorradorCarro,
	nuevoBorradorNota,
	nuevoBorradorNotaPrecinto,
	nuevoBorradorCerrar,
	nuevoBorradorAnularCarga,
	nuevoBorradorAnularPallet,
	nuevoBorradorCorregirCarga,
	nuevoBorradorCerrarPallet,
	nuevoBorradorDescarte,
	nuevoBorradorEmbolsar,
	leerHoja,
	listarHojas,
	nuevaCorreccion,
	nuevaHoja,
	nuevoBorradorParada,
	recalcularAvisos,
	nuevoBorradorTurno,
	recalcularPallets,
	recalcularRotulosEnBandeja,
	recalcularParaMuestra,
	recalcularSilos,
	silosAbiertos,
} from './ui/estado';
import { pintarCaptura } from './ui/pantallas-captura';
import { pintarDescarte } from './ui/pantallas-descarte';
import { arrancarEn, elegirSilo, elegirSiloDeNota, pasosDe, pintarInicioProduccion, pintarListoProduccion, pintarProduccion } from './ui/pantallas-produccion';
import { pintarCorregirHumedad, pintarHoja, pintarHojaCorregida, pintarHumedad, pintarListaCalidad, pintarListoCalidad, pintarVerHoja } from './ui/pantallas-calidad';
import {
	arrancarEn as arrancarEnvasadoEn,
	elegirPallet,
	elegirPrecinto,
	irATarima,
	pintarCerradosEnvasado,
	pintarEnvasado,
	pintarInicioEnvasado,
	pintarListoEnvasado,
} from './ui/pantallas-envasado';
import {
	pintarAdvertenciaReloj,
	pintarConfig,
	pintarDesenrolado,
	pintarEnrolar,
	pintarGuardado,
	pintarPendientes,
	pintarRetomar,
	pintarSinEstacion,
} from './ui/pantallas-sistema';

const raiz = document.getElementById('app');

/** v6: cuánto vale un catálogo con una pantalla que mira lo que hacen los otros teléfonos. */
const CATALOGO_AL_DIA_MS = 2 * 60 * 1000;

function conCatalogoAlDia(): boolean {
	if (app.vista.tipo === 'produccion-inicio') return true;
	// Producción, eligiendo el carro (6/10): «ya bajó» mira las bajadas de los otros teléfonos y los viajes de verde.
	if (app.vista.tipo === 'produccion' && app.vista.borrador.tipo === 'carro') return pasosDe(app.vista.borrador)[app.vista.borrador.paso] === 'carro';
	// Verde, eligiendo carros: el aviso de «no figura bajado» mira lo que subió el otro teléfono.
	return app.vista.tipo === 'captura' && app.vista.borrador.tipo === 'viaje' && app.vista.borrador.paso === 2;
}

async function arrancar(): Promise<void> {
	if (!raiz) return;
	if ('serviceWorker' in navigator) {
		void navigator.serviceWorker.register('/sw.js');
	}

	const bd = await abrirBd();
	const preferencias = ((await leerConfig<Preferencias>(bd, 'preferencias')) ?? {}) as Preferencias;

	const planificador = crearPlanificador(async () => {
		// v6 (§12.2, §12.3): con el inicio de producción a la vista (los avisos de la otra encargada) o
		// eligiendo carros en verde (lo que trajo el otro teléfono), un catálogo de más de dos minutos se renueva.
		const resultado = await sincronizar(app.bd, fetch, { catalogosVigentesMs: conCatalogoAlDia() ? CATALOGO_AL_DIA_MS : undefined });
		// v6 (§12.7): un reinicio de temporada purgó todo menos la sesión; la app arranca de nuevo.
		if (resultado === 'reiniciado') {
			window.location.reload();
			return resultado;
		}
		await despuesDelSync();
		return resultado;
	});

	iniciarApp({
		bd,
		sesion: null,
		catalogos: null,
		preferencias,
		enLinea: navigator.onLine,
		contadores: { pendientes: 0, incidentes: 0, masViejaMs: null },
		render: pintar,
		dispararSync: planificador.disparar,
	});

	// storage.persist(): si el navegador lo niega, advertencia permanente (§5.1).
	if (navigator.storage?.persist) {
		void navigator.storage.persist().then((ok) => {
			app.preferencias.persistencia_ok = ok;
			void actualizarPreferencias(bd, { persistencia_ok: ok });
		});
	}

	// Un enrolamiento cortado a mitad (crash tras consumir el código) se retoma.
	await reintentarEnrolamientoPendiente(bd);

	app.sesion = (await leerConfig<Sesion>(bd, 'sesion')) ?? null;
	if (!app.sesion) {
		irA({ tipo: 'enrolar' });
		return;
	}
	app.catalogos = ((await leerCatalogos(bd)) ?? null) as CatalogosLocales | null;
	await purgarRecibosViejos(bd, Date.now(), preferencias.catalogos_hora_servidor);
	await refrescarContadores();

	window.addEventListener('online', () => {
		app.enLinea = true;
		planificador.disparar();
		app.render();
	});
	window.addEventListener('offline', () => {
		app.enLinea = false;
		app.render();
	});
	setInterval(() => {
		if (conRegistroEnCurso()) pintarEncabezado();
	}, 20_000);
	// Las listas que mueven OTROS teléfonos —los silos abiertos de producción, los que
	// esperan muestra en calidad— no se refrescaban solas con la app quieta: el sync
	// corre al guardar y al volver la señal, nada más. Con la lista a la vista se pide
	// uno cada cinco minutos (baja catálogos solo si tienen más de diez) y al volver
	// al frente (Codex, ronda del plan de calidad, 11/9).
	// Verde no tiene lista: su «inicio» es «¿quién descarga?» sin nada cargado, y ahí el
	// lote nuevo también tiene que aparecer solo (revisión del 12/9).
	const conListaALaVista = () =>
		app.vista.tipo === 'calidad-lista' ||
		app.vista.tipo === 'produccion-inicio' ||
		// Los precintos con letra los mueve calidad, y los pallets en armado, el otro teléfono de envasado.
		app.vista.tipo === 'envasado-inicio' ||
		// §12.18: los cerrados de las últimas 48 horas, que cierran y corrigen los otros teléfonos.
		app.vista.tipo === 'envasado-cerrados' ||
		// «Verde, últimas 24 horas» (§5.5) y «Producción, últimas 24 horas» (§12.20): lo que cargaron los otros teléfonos.
		(app.vista.tipo === 'pendientes' && (app.sesion?.estacion === 'verde' || app.sesion?.estacion === 'produccion')) ||
		// Solo el viaje que todavía no tiene nada: un viaje entero al que se volvió con
		// «cambiar» también está en el paso 0, y no es un inicio (Codex, 13/9).
		(app.vista.tipo === 'captura' && app.vista.borrador.paso === 0 && app.vista.borrador.lote_id === null && app.vista.borrador.carros.length === 0);
	setInterval(() => {
		if (document.visibilityState === 'visible' && conListaALaVista()) planificador.disparar();
	}, 5 * MINUTO_MS);
	// v6 (§12.2): el inicio de producción se refresca más seguido: un aviso de la que sale no espera cinco minutos.
	setInterval(() => {
		if (document.visibilityState === 'visible' && app.vista.tipo === 'produccion-inicio') planificador.disparar();
	}, CATALOGO_AL_DIA_MS);
	document.addEventListener('visibilitychange', () => {
		if (document.visibilityState === 'visible' && conListaALaVista()) planificador.disparar();
	});

	acciones.nuevaCaptura = () => {
		// Cada estación abre en lo suyo. La que todavía no tiene flujo (despacho) no
		// captura nada que el servidor vaya a rechazar por `estacion_no_autorizada`.
		if (app.sesion?.estacion === 'produccion') {
			acciones.inicioProduccion();
			return;
		}
		if (app.sesion?.estacion === 'calidad') {
			acciones.inicioCalidad();
			return;
		}
		if (app.sesion?.estacion === 'envasado') {
			acciones.inicioEnvasado();
			return;
		}
		if (app.sesion?.estacion !== 'verde') {
			irA({ tipo: 'sin-estacion', estacion: app.sesion?.estacion ?? null });
			return;
		}
		void listarBorradores().then((borradores) => {
			// Lo que quedó a medias se ofrece ANTES de abrir otro registro: si no,
			// un segundo borrador solo reaparecía al recargar la app. El aviso del reloj
			// envuelve lo que venga, retomar incluido: un borrador retomado con el reloj
			// cambiado escribe horas corridas igual (Codex, 21/9).
			irA(conAvisoDeReloj(borradores.length > 0 ? { tipo: 'retomar', borradores } : { tipo: 'captura', borrador: nuevoBorrador() }));
		});
	};
	// «Descarte»: la segunda tarea de la estación verde. Lo firma UNA persona
	// —quien lo lleva al monte— y NO se hereda del viaje (8/9): el que descargó el
	// camión no es necesariamente el que lo lleva, y arrastrarlo firmaba con el nombre
	// equivocado sin preguntar. El borrador nace con la premarcación del dueño del
	// teléfono y la pantalla pregunta igual: es un toque, y el nombre queda bien.
	// La única puerta a «Lo cargado» (§5.5, verde reciente; §12.20, producción reciente): en verde y en producción
	// la pregunta de quien entra es «¿qué hay ahora?», así que se debe un refresco de catálogos antes de pintar (si no
	// entra, queda debido y la pasada siguiente lo pide); el pintado no dispara nada, y el repintado de la respuesta
	// no vuelve a pedir.
	acciones.verLoCargado = () => {
		if (app.sesion?.estacion === 'verde' || app.sesion?.estacion === 'produccion') {
			app.preferencias.catalogos_por_refrescar = true;
			void actualizarPreferencias(app.bd, { catalogos_por_refrescar: true })
				.catch((error) => console.error('no se pudo pedir el refresco de catálogos', error))
				.then(() => app.dispararSync());
		}
		irA({ tipo: 'pendientes' });
	};
	acciones.nuevoDescarte = () => {
		irA({ tipo: 'descarte', borrador: nuevoBorradorDescarte() });
	};

	// v4 · producción. Lo que quedó a medias se ofrece ANTES de abrir otro registro,
	// igual que en verde: un borrador a medias que no se ve es un dato que se pierde.
	acciones.inicioProduccion = () => {
		void listarBorradores().then((borradores) => {
			// Con el aviso del reloj delante si hace falta: una parada fechada con el reloj
			// corrido cae en el turno que no es (revisión externa, 21/9).
			irA(conAvisoDeReloj(borradores.length > 0 ? { tipo: 'retomar', borradores } : { tipo: 'produccion-inicio' }));
		});
	};
	// Todo borrador de producción nace en el paso 0 y arranca en su primer paso visible
	// (`arrancarEn`): una sola fuente de verdad para qué se saltea (P12, 12/9).
	const empezarProduccion = (borrador: BorradorProduccion): void => {
		arrancarEn(borrador);
		irA({ tipo: 'produccion', borrador });
	};
	acciones.abrirSilo = () => empezarProduccion(nuevoBorradorAbrir());
	acciones.cambioDeTurno = () => {
		// Congela la foto de los abiertos de AHORA: el «desde», el bruto anterior y la
		// tara de cada uno salen de acá, no del catálogo de dentro de un rato.
		const abiertos = silosAbiertos();
		if (abiertos.length > 0) empezarProduccion(nuevoBorradorTurno(abiertos));
	};
	acciones.cerrarSilo = (silo) => {
		const borrador = nuevoBorradorCerrar();
		if (silo) {
			// Tocar la tarjeta ya elige el silo: se congela su foto y el paso se saltea.
			borrador.desde_inicio = true;
			elegirSilo(borrador, silo);
			arrancarEn(borrador);
			// `elegirSilo` ya guardó, pero con el paso anterior: si la app se cierra antes
			// del primer dígito, retomar volvía a ofrecer el silo (Codex, 11/9).
			void guardarBorrador(borrador);
		}
		empezarProduccion(borrador);
	};
	acciones.cambioDeCarro = () => {
		empezarProduccion(nuevoBorradorCarro());
		// v6 (6/10): lo que dice cada carro lo mueven los otros teléfonos; al entrar se pide un refresco
		// (el sync lo hace solo si el catálogo tiene más de dos minutos), como la lista de calidad.
		app.dispararSync();
	};
	acciones.parada = () => empezarProduccion(nuevoBorradorParada());
	// v6 (§12.2, §12.4)
	acciones.dejarAviso = () => empezarProduccion(nuevoBorradorAviso());
	acciones.notaASilo = (silo) => {
		const borrador = nuevoBorradorNota();
		if (silo) elegirSiloDeNota(borrador, silo);
		empezarProduccion(borrador);
	};

	// v4 · calidad. La lista es el centro: varias hojas a medias a la vez (la humedad
	// tarda ocho minutos), así que no hay «¿seguís con…?» — cada silo muestra la suya.
	acciones.inicioCalidad = () => {
		void listarHojas().then((hojas) => {
			irA(conAvisoDeReloj({ tipo: 'calidad-lista', hojas }));
			// La lista la mueven otros teléfonos: al entrar se pide un refresco (el sync lo
			// hace solo si el catálogo tiene más de diez minutos). SOLO al entrar: después
			// de un sync se recarga la lista sin pedir otro, o el sync se llamaría a sí
			// mismo para siempre (Codex, ronda del diff de calidad, 11/9).
			app.dispararSync();
		});
	};
	acciones.abrirHoja = (silo) => {
		void leerHoja(silo.llenado_id).then((hoja) => {
			const borrador = hoja ?? nuevaHoja(silo);
			if (!hoja) void guardarBorrador(borrador);
			irA({ tipo: 'calidad-hoja', borrador });
		});
	};
	// §12.18: «Cargadas · últimas 48 h». Corregir retoma la corrección a medias de esa versión; una a medias sobre otra
	// versión del silo ya no entraría (`ya_corregido`) y se descarta.
	acciones.verHoja = (hoja) => irA({ tipo: 'calidad-ver-hoja', hoja });
	acciones.corregirHoja = (hoja) => {
		void listarHojas().then(async (hojas) => {
			const previas = hojas.filter((b) => b.corrige?.llenado_id === hoja.llenado_id);
			const retomada = previas.find((b) => b.corrige?.muestra_id === hoja.muestra_id);
			for (const vieja of previas) if (vieja !== retomada) await descartarBorrador(vieja).catch(() => null);
			// La base al día: el padre que se fijó al empezar pudo subir en el medio.
			const borrador = retomada ? { ...retomada, corrige: hoja } : nuevaCorreccion(hoja);
			await guardarBorrador(borrador);
			irA({ tipo: 'calidad-hoja', borrador });
		});
	};

	// v5 · envasado. Como producción: lo que quedó a medias se ofrece antes de abrir otro
	// registro, y todo borrador nace en el paso 0 y arranca en su primer paso visible.
	acciones.inicioEnvasado = () => {
		void listarBorradores().then((borradores) => {
			irA(conAvisoDeReloj(borradores.length > 0 ? { tipo: 'retomar', borradores } : { tipo: 'envasado-inicio' }));
		});
	};
	const empezarEnvasado = (borrador: BorradorEmbolsado): void => {
		arrancarEnvasadoEn(borrador);
		irA({ tipo: 'envasado', borrador });
	};
	acciones.embolsar = (precinto) => {
		const borrador = nuevoBorradorEmbolsar();
		if (precinto) {
			// Tocar la tarjeta del precinto ya lo elige: se congela lo visto y el paso se saltea.
			borrador.desde_inicio = true;
			elegirPrecinto(borrador, precinto);
			arrancarEnvasadoEn(borrador);
			void guardarBorrador(borrador);
		}
		empezarEnvasado(borrador);
	};
	acciones.cerrarPallet = (pallet) => {
		const borrador = nuevoBorradorCerrarPallet();
		if (pallet) {
			// Tocar la tarjeta ya elige el pallet: se congela su foto (la base del CAS y el
			// testigo de composición) y el paso se saltea.
			borrador.desde_inicio = true;
			elegirPallet(borrador, pallet);
			arrancarEnvasadoEn(borrador);
			void guardarBorrador(borrador);
		}
		empezarEnvasado(borrador);
	};
	// Segunda tanda (§5.7 r. 17c, 18 i): la tarima de espera es un embolsado sin letra; «se tira un pallet» pregunta cuál.
	// Pasarla a un pallet es embolsar el precinto cuando la letra llegó (§12.21): no hay flujo aparte.
	acciones.tarimaDeEspera = () => {
		const borrador = nuevoBorradorEmbolsar();
		irATarima(borrador, true);
		empezarEnvasado(borrador);
	};
	// v6 (§12.5, §12.4): desde el precinto elegido.
	acciones.cambiarLetra = (precinto) => empezarEnvasado(nuevoBorradorCambiarLetra(precinto));
	acciones.notaPrecinto = (precinto) => empezarEnvasado(nuevoBorradorNotaPrecinto(precinto));
	// §12.18: los cerrados de las últimas 48 horas (se refrescan al entrar), y corregir o anular una carga tocándola.
	acciones.verCerrados = () => {
		app.dispararSync();
		irA({ tipo: 'envasado-cerrados' });
	};
	acciones.corregirCarga = (carga) => empezarEnvasado(nuevoBorradorCorregirCarga(carga));
	acciones.anularCarga = (carga) => empezarEnvasado(nuevoBorradorAnularCarga(carga));
	acciones.tirarPallet = (pallet) => {
		const borrador = nuevoBorradorAnularPallet();
		if (pallet) {
			borrador.desde_inicio = true;
			elegirPallet(borrador, pallet);
			arrancarEnvasadoEn(borrador);
			void guardarBorrador(borrador);
		}
		empezarEnvasado(borrador);
	};

	planificador.disparar();

	// Las proyecciones, antes del primer pintado: el inicio de producción y la lista de
	// calidad se dibujan con lo que hay en la cola aunque el sync todavía no haya vuelto.
	recalcularSilos(await mutacionesDeProduccion(bd, app.preferencias.catalogos_hora_servidor), await historiaDeProduccion(bd));
	// v6 (§12.2): los «Entendido» de este teléfono, y los avisos con la cola encima.
	app.avisosEntendidos = new Set(((await leerConfig<string[]>(bd, 'avisos_entendidos')) ?? []) as string[]);
	recalcularAvisos(await mutacionesDeAvisos(bd, app.preferencias.catalogos_hora_servidor));
	app.viajesLocales = await mutacionesDeVerde(bd, app.preferencias.catalogos_hora_servidor);
	recalcularParaMuestra(await mutacionesDeCalidad(bd, app.preferencias.catalogos_hora_servidor));
	recalcularPallets(await mutacionesDeEmbolsado(bd, app.preferencias.catalogos_hora_servidor));
	await recalcularRotulosEnBandeja();

	if (app.preferencias.desenrolado) {
		irA({ tipo: 'desenrolado' });
		return;
	}
	acciones.nuevaCaptura(); // ofrece lo que quedó a medias antes de abrir otro registro
}

/** El chip de la cola cuenta también las fotos del precinto: una foto que espera o que falló no puede convivir con un «0». */
async function refrescarContadores(): Promise<void> {
	const [cola, fotos, incidentes] = await Promise.all([contarPendientes(app.bd), contarFotos(app.bd), bandeja(app.bd)]);
	app.rechazos = new Map(incidentes.map((f) => [f.id, f.motivo ?? '']));
	const masVieja = [cola.masViejaMs, fotos.fotoMasViejaMs].filter((ms): ms is number => ms !== null);
	app.contadores = {
		pendientes: cola.pendientes + fotos.fotos,
		incidentes: cola.incidentes + fotos.fotosFalladas,
		masViejaMs: masVieja.length === 0 ? null : Math.min(...masVieja),
	};
}

/** Tras cada sync: contadores, catálogos, sesión y preferencias frescos y, si la vista «guardado» espera folio, buscarlo. */
async function despuesDelSync(): Promise<void> {
	await refrescarContadores();
	// El sync puede haber bajado catálogos nuevos, realineado la estación y
	// siempre reescribe preferencias (desvío del reloj, desenrolado, epochs
	// congelados, último error): las copias en memoria de la UI se realinean acá.
	app.catalogos = ((await leerCatalogos(app.bd)) ?? app.catalogos) as CatalogosLocales | null;
	app.sesion = (await leerConfig<Sesion>(app.bd, 'sesion')) ?? app.sesion;
	app.preferencias = ((await leerConfig<Preferencias>(app.bd, 'preferencias')) ?? app.preferencias) as Preferencias;
	if (app.preferencias.desenrolado && app.vista.tipo !== 'enrolar') {
		irA({ tipo: 'desenrolado' });
		return;
	}
	// Gestión pudo cambiar la estación: si dejó de ser verde, el flujo de verde se
	// cierra acá mismo — lo ya encolado sigue subiendo, lo nuevo no se captura. Con el
	// aviso del reloj a la vista, lo que cuenta es la pantalla que espera detrás de él.
	const efectiva = app.vista.tipo === 'advertencia-reloj' ? app.vista.luego : app.vista;
	if (app.sesion && app.sesion.estacion !== 'verde' && (efectiva.tipo === 'captura' || efectiva.tipo === 'descarte')) {
		irA({ tipo: 'sin-estacion', estacion: app.sesion.estacion });
		return;
	}
	// Y al revés: un teléfono que dejó de ser de producción no sigue en sus pantallas.
	if (app.sesion && app.sesion.estacion !== 'produccion' && efectiva.tipo.startsWith('produccion')) {
		irA({ tipo: 'sin-estacion', estacion: app.sesion.estacion });
		return;
	}
	if (app.sesion && app.sesion.estacion !== 'calidad' && efectiva.tipo.startsWith('calidad')) {
		irA({ tipo: 'sin-estacion', estacion: app.sesion.estacion });
		return;
	}
	if (app.sesion && app.sesion.estacion !== 'envasado' && efectiva.tipo.startsWith('envasado')) {
		irA({ tipo: 'sin-estacion', estacion: app.sesion.estacion });
		return;
	}
	// Lo que quedó a medias es de una estación: si el teléfono cambió de estación con «¿seguís
	// con…?» a la vista, se vuelve a ofrecer lo de la estación nueva (Codex, ronda del diff, 14/9).
	if (efectiva.tipo === 'retomar' && app.sesion && efectiva.borradores.some((b) => estacionDeBorrador(b.tipo) !== app.sesion?.estacion)) {
		acciones.nuevaCaptura();
		return;
	}
	// Las proyecciones se rehacen con lo que haya en la cola y en los recibos:
	// después de un sync, lo que entró ya está confirmado y lo que quedó, pendiente.
	recalcularSilos(await mutacionesDeProduccion(app.bd, app.preferencias.catalogos_hora_servidor), await historiaDeProduccion(app.bd));
	app.avisosEntendidos = new Set(((await leerConfig<string[]>(app.bd, 'avisos_entendidos')) ?? []) as string[]);
	recalcularAvisos(await mutacionesDeAvisos(app.bd, app.preferencias.catalogos_hora_servidor));
	app.viajesLocales = await mutacionesDeVerde(app.bd, app.preferencias.catalogos_hora_servidor);
	recalcularParaMuestra(await mutacionesDeCalidad(app.bd, app.preferencias.catalogos_hora_servidor));
	recalcularPallets(await mutacionesDeEmbolsado(app.bd, app.preferencias.catalogos_hora_servidor));
	await recalcularRotulosEnBandeja();
	// Con el aviso del reloj a la vista, el desvío recién medido decide solo: si ya está bien
	// se avanza a lo que esperaba, y si sigue mal se actualizan los minutos. Antes, «Ya la
	// arreglé» volvía a mostrar el aviso viejo hasta un segundo reload.
	if (app.vista.tipo === 'advertencia-reloj') {
		irA(conAvisoDeReloj(app.vista.luego));
		return;
	}
	// La lista de calidad se pinta con las hojas que había al entrar: si cambió el
	// catálogo, se vuelve a armar con las hojas de ahora — sin pedir otro sync, y solo
	// si la analista SIGUE en la lista cuando las hojas terminan de leerse: si en el
	// medio abrió una hoja, no se la saca de ahí (Codex, ronda del diff, 11/9).
	if (app.vista.tipo === 'calidad-lista') {
		const hojas = await listarHojas();
		if (app.vista.tipo === 'calidad-lista') irA({ tipo: 'calidad-lista', hojas });
		return;
	}
	if (app.vista.tipo === 'guardado' && !app.vista.enviado) {
		// Un registro recién creado es su propia raíz: el recibo está bajo su id.
		const registroId = app.vista.registroId;
		const recibo = (await recibos(app.bd)).find((r) => r.raiz_id === registroId);
		if (recibo) {
			irA({ ...app.vista, nroViaje: recibo.nro_viaje, enviado: true });
			return;
		}
	}
	// Un sync que termina mientras se tipea (la nota de verde, el detalle de la parada,
	// el motivo de calidad) no reconstruye la pantalla: se llevaría el foco y lo tipeado
	// a medias. Solo el encabezado (Codex, ronda del diff de la revisión, 13/9).
	const activo = document.activeElement;
	if (activo instanceof HTMLTextAreaElement || activo instanceof HTMLInputElement) pintarEncabezado();
	else app.render();
}

let encabezado: HTMLElement | null = null;

/** El wire no lleva tildes (`produccion`); la pantalla sí. */
const NOMBRE_ESTACION: Record<string, string> = { verde: 'verde', produccion: 'producción', calidad: 'calidad', envasado: 'envasado', despacho: 'despacho' };

/** Las vistas con un registro a medias: en todas, el encabezado dice quién lo carga (disposición 11). */
function conRegistroEnCurso(): boolean {
	const t = app.vista.tipo;
	return t === 'captura' || t === 'descarte' || t === 'produccion' || t === 'calidad-hoja' || t === 'envasado';
}

function pintarEncabezado(): void {
	if (!encabezado) return;
	encabezado.textContent = '';
	const titulo = el('div', { class: 'marca' });
	titulo.appendChild(el('b', undefined, app.sesion?.estacion ? `Estación ${NOMBRE_ESTACION[app.sesion.estacion] ?? app.sesion.estacion}` : 'Perejil'));
	titulo.appendChild(el('span', { class: 'horita' }, horaCorta(ahoraMs())));
	// Teléfono que se pasa de mano (disposición 11): las personas del registro
	// en curso quedan siempre visibles mientras se captura.
	const vista = app.vista;
	const enCurso = conRegistroEnCurso() && 'borrador' in vista ? vista.borrador : null;
	if (enCurso && enCurso.personas.length > 0 && app.catalogos) {
		const nombres = enCurso.personas
			.map((id) => app.catalogos?.personas.find((p) => p.id === id)?.nombre ?? '')
			.filter((n) => n.length > 0)
			.join(' y ');
		if (nombres) titulo.appendChild(el('span', { class: 'quienes' }, nombres));
	}
	// Config del encargado: mantener apretado 3 s el encabezado.
	let inicio: number | null = null;
	titulo.addEventListener('pointerdown', () => {
		inicio = Date.now();
	});
	titulo.addEventListener('pointerup', () => {
		if (inicio !== null && Date.now() - inicio >= 3000) irA({ tipo: 'config' });
		inicio = null;
	});
	encabezado.appendChild(titulo);

	const derecha = el('div', { class: 'derecha' });
	if (!app.enLinea) {
		derecha.appendChild(el('span', { class: 'pill-off' }, 'sin señal'));
	}
	// El número del chip cuenta REGISTROS esperando señal; un incidente levanta la alerta.
	const { pendientes, incidentes } = app.contadores;
	const alerta = incidentes > 0;
	// El chip está siempre, también en cero: en verde es la única puerta a «lo cargado» y a
	// «actualizar listas», porque verde no tiene inicio (revisión del 12/9; el POC lo mostraba «↑ 0»).
	// Sin sesión (enrolando) no hay adónde ir ni desde dónde volver: el chip no está.
	if (app.sesion) {
		const chipPendientes = el('button', { class: alerta ? 'pill-cola alerta' : 'pill-cola', type: 'button', 'aria-label': 'Lo cargado en este teléfono' });
		chipPendientes.appendChild(icono('subir', 12, 2.4));
		chipPendientes.appendChild(el('span', undefined, alerta ? '¡!' : String(pendientes)));
		chipPendientes.onclick = () => acciones.verLoCargado();
		derecha.appendChild(chipPendientes);
	}
	encabezado.appendChild(derecha);
}

/**
 * Qué pantalla es, a los efectos del scroll: misma clave = misma pantalla repintada
 * (un dígito más, un defecto marcado), y el cuerpo vuelve a donde estaba. Otra clave
 * = otra pantalla, que arranca arriba. Es lo que hace el boceto.
 */
function claveDePantalla(vista: typeof app.vista): string {
	const borrador = 'borrador' in vista ? vista.borrador : null;
	const donde = borrador === null ? '' : 'pantalla' in borrador ? borrador.pantalla : 'paso' in borrador ? String(borrador.paso) : '';
	return `${vista.tipo}|${borrador?.capture_id ?? ''}|${donde}`;
}

let ultimaPantalla: string | null = null;

function pintar(): void {
	if (!raiz) return;
	// Repintar la misma pantalla no puede mandar el scroll arriba: con la lista de
	// defectos o el teclado debajo del pliegue, cada toque volvía al principio
	// (Codex, ronda del diff de calidad, 11/9).
	const clave = claveDePantalla(app.vista);
	const cuerpoAnterior = raiz.querySelector('.cuerpo');
	const scroll = clave === ultimaPantalla && cuerpoAnterior ? cuerpoAnterior.scrollTop : 0;
	ultimaPantalla = clave;
	raiz.textContent = '';
	encabezado = el('header', { class: 'encabezado' });
	raiz.appendChild(encabezado);
	pintarEncabezado();

	const cuerpo = el('div', { class: 'cuerpo' });
	const pie = el('div', { class: 'pie' });
	raiz.appendChild(cuerpo);
	raiz.appendChild(pie);
	if (scroll > 0) requestAnimationFrame(() => cuerpo.scrollTo(0, scroll));

	const vista = app.vista;
	switch (vista.tipo) {
		case 'cargando':
			cuerpo.appendChild(el('div', { class: 'qsub centrado arriba' }, 'Cargando…'));
			return;
		case 'enrolar':
			return pintarEnrolar(cuerpo, vista);
		case 'retomar':
			return pintarRetomar(cuerpo, pie, vista);
		case 'advertencia-reloj':
			return pintarAdvertenciaReloj(cuerpo, pie, vista);
		case 'captura':
			// Teléfono compartido (disposición 11): una captura empezada y abandonada
			// más de 10 minutos vuelve a preguntar de quién es antes de seguir.
			if (vista.borrador.paso > 0 && Date.now() - vista.borrador.actualizado_en > 10 * MINUTO_MS) {
				// Con los demás a medias detrás, para que retomar no los esconda (13/9).
				const actual = vista.borrador;
				void listarBorradores().then((todos) =>
					irA({ tipo: 'retomar', borradores: [actual, ...todos.filter((b) => b.capture_id !== actual.capture_id)] }),
				);
				return;
			}
			return pintarCaptura(cuerpo, pie, vista.borrador);
		case 'descarte':
			return pintarDescarte(cuerpo, pie, vista.borrador);
		case 'guardado':
			return pintarGuardado(cuerpo, pie, vista);
		case 'pendientes':
			void pintarPendientes(cuerpo, pie);
			return;
		case 'config':
			void pintarConfig(cuerpo, pie);
			return;
		case 'desenrolado':
			return pintarDesenrolado(cuerpo, pie);
		case 'sin-estacion':
			return pintarSinEstacion(cuerpo, pie, vista);
		case 'produccion-inicio':
			return pintarInicioProduccion(cuerpo, pie, vista.aviso ?? null);
		case 'produccion':
			return pintarProduccion(cuerpo, pie, vista.borrador);
		case 'produccion-listo':
			return pintarListoProduccion(cuerpo, pie, vista.borrador, vista.filas);
		case 'calidad-lista':
			return pintarListaCalidad(cuerpo, pie, vista);
		case 'calidad-hoja':
			return pintarHoja(cuerpo, pie, vista.borrador);
		case 'calidad-humedad':
			return pintarHumedad(cuerpo, pie, vista);
		case 'calidad-corregir-humedad':
			return pintarCorregirHumedad(cuerpo, pie, vista);
		case 'calidad-ver-hoja':
			return pintarVerHoja(cuerpo, pie, vista);
		case 'calidad-hoja-corregida':
			return pintarHojaCorregida(cuerpo, pie, vista);
		case 'calidad-listo':
			return pintarListoCalidad(cuerpo, pie, vista.borrador, vista.letra);
		case 'envasado-inicio':
			return pintarInicioEnvasado(cuerpo, pie, vista.aviso ?? null);
		case 'envasado':
			return pintarEnvasado(cuerpo, pie, vista.borrador);
		case 'envasado-listo':
			return pintarListoEnvasado(cuerpo, pie, vista.borrador);
		case 'envasado-cerrados':
			return pintarCerradosEnvasado(cuerpo, pie);
	}
}

void arrancar();
